import React from 'react';
import {
  TrendingUp,
  TrendingDown,
  Target,
  AlertTriangle,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';

interface MetricCardProps {
  label: string;
  value: string;
  subValue?: string;
  trend?: 'up' | 'down' | 'neutral' | 'warning';
  trendLabel?: string;
  icon: React.ReactNode;
  colSpan?: string;
  variant?: 'default' | 'hero' | 'warning' | 'negative';
}

function MetricCard({
  label,
  value,
  subValue,
  trend,
  trendLabel,
  icon,
  colSpan = '',
  variant = 'default',
}: MetricCardProps) {
  const variantStyles = {
    default: 'bg-card border-border card-glow-primary',
    hero: 'bg-card border-primary/20 card-glow-primary',
    warning: 'bg-warning-subtle border-warning/30 card-glow-warning',
    negative: 'bg-negative-subtle border-negative/30 card-glow-negative',
  };

  const trendColors = {
    up: 'text-positive bg-positive-subtle',
    down: 'text-negative bg-negative-subtle',
    neutral: 'text-muted-foreground bg-muted',
    warning: 'text-warning bg-warning-subtle',
  };

  return (
    <div className={`rounded-xl border p-5 ${variantStyles[variant]} ${colSpan} transition-all duration-200 hover:shadow-card-hover`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-lg ${
          variant === 'hero' ? 'bg-primary/10 text-primary' :
          variant === 'warning' ? 'bg-warning/10 text-warning' :
          variant === 'negative'? 'bg-negative/10 text-negative' : 'bg-muted text-muted-foreground'
        }`}>
          {icon}
        </div>
        {trend && trendLabel && (
          <span className={`flex items-center gap-1 text-xs font-500 px-2 py-1 rounded-full ${trendColors[trend]}`}>
            {trend === 'up' && <ArrowUpRight size={10} />}
            {trend === 'down' && <ArrowDownRight size={10} />}
            {trend === 'warning' && <AlertTriangle size={10} />}
            {trendLabel}
          </span>
        )}
      </div>
      <p className="text-xs font-500 text-muted-foreground uppercase tracking-wider mb-1">{label}</p>
      <p className={`font-mono-nums font-bold leading-none mb-1 ${
        variant === 'hero' ? 'text-metric-xl text-foreground' : 'text-metric-lg text-foreground'
      }`}>
        {value}
      </p>
      {subValue && (
        <p className="text-xs text-muted-foreground font-mono-nums">{subValue}</p>
      )}
    </div>
  );
}

export default function MetricsBentoGrid() {
  // Grid plan: 7 cards → grid-cols-4
  // Row 1: hero (spans 2 cols) + 2 regular = 4 cols
  // Row 2: 4 regular cards
  // No orphans

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
      {/* Hero: Net P&L — spans 2 cols */}
      <MetricCard
        label="Net P&L"
        value="+$14,382"
        subValue="This month · 47 trades"
        trend="up"
        trendLabel="+23.4% vs last month"
        icon={<DollarSign size={18} />}
        colSpan="sm:col-span-2"
        variant="hero"
      />

      {/* Win Rate */}
      <MetricCard
        label="Win Rate"
        value="63.8%"
        subValue="30W / 17L this month"
        trend="up"
        trendLabel="+2.1%"
        icon={<Target size={18} />}
      />

      {/* Profit Factor */}
      <MetricCard
        label="Profit Factor"
        value="2.41"
        subValue="Gross $24,190 / $10,034"
        trend="up"
        trendLabel="Strong"
        icon={<TrendingUp size={18} />}
      />

      {/* Avg R:R */}
      <MetricCard
        label="Avg Risk:Reward"
        value="1 : 2.8"
        subValue="Target ≥ 1:2.0"
        trend="up"
        trendLabel="On target"
        icon={<ArrowUpRight size={18} />}
      />

      {/* Max Drawdown — warning state */}
      <MetricCard
        label="Max Drawdown"
        value="-$3,210"
        subValue="Aug 12–14 · 3 consecutive losses"
        trend="warning"
        trendLabel="Monitor"
        icon={<AlertTriangle size={18} />}
        variant="warning"
      />

      {/* Avg Win */}
      <MetricCard
        label="Avg Win"
        value="+$806"
        subValue="Per winning trade"
        trend="up"
        trendLabel="+$42 vs last month"
        icon={<TrendingUp size={18} />}
      />

      {/* Avg Loss */}
      <MetricCard
        label="Avg Loss"
        value="-$590"
        subValue="Per losing trade"
        trend="down"
        trendLabel="-$38 vs last month"
        icon={<TrendingDown size={18} />}
        variant="negative"
      />
    </div>
  );
}